import { isParseObject, useFeedback } from "@opendash/core";
import { Source } from "@opendash/core/dist/types-generated";
import { useTranslation } from "@opendash/i18n";
import { Icon } from "@opendash/icons";
import { useNavigate } from "@opendash/router";
import { Button, Drawer, Flex, Modal, Popconfirm } from "antd";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React, { useEffect, useState } from "react";
import { Media, Message, Schedule_Execution } from "../../../js/parsegenerated";
import { MessageDynamicDescription } from "../../messages";
import { MessageForm } from "../../messages/components/MessageForm";
import SpontaneousScheduleModal from "../../schedules/components/SpontaneousScheduleModal";
import {
  TicketContextProvider,
  TicketData,
  TicketDynamicDescription,
  useTicketManager,
} from "../../tickets";
import { MachinelogContext } from "../context/MachinelogContext";
import { getMessageType } from "../helpers/helpers";
import ScheduleExecutionDetails from "./ScheduleExecutionDetails";

type Props = {
  message: Message;
};

export const MessageControl = observer((props: Props) => {
  const t = useTranslation();
  const nav = useNavigate();
  const { message } = useFeedback();
  const [openViewModal, setOpenViewModal] = React.useState<boolean>(false);
  const [openEditModal, setOpenEditModal] = React.useState<boolean>(false);
  const machinelogState = React.useContext(MachinelogContext);
  const [origin, setOrigin] = useState<
    Message | TicketData | Schedule_Execution
  >(props.message);
  const ticketManager = useTicketManager();

  useEffect(() => {
    async function fetchOrigin() {
      const type = getMessageType(props.message);
      const data = props.message.data;
      if (type === "ticket" && data.origin) {
        ticketManager.addFilter("id", {
          attribute: "objectId",
          value: data.origin.id as string,
        });
        const response = await ticketManager.fetchTickets();
        setOrigin(response.data?.[0]);
      }
      if (type === "schedule" && data.origin) {
        const scheduleExe = await new Parse.Query(Schedule_Execution).get(
          data.origin.id as string
        );
        setOrigin(scheduleExe);
      }
      if (type === "message" || !data.origin) {
        setOrigin(props.message);
      }
    }
    fetchOrigin();
  }, [props.message]);

  async function removeMessage() {
    try {
      const media =
        (await props.message.relation("media")?.query().find()) ?? [];
      await Media.destroyAll(media);
      await props.message.destroy();
    } catch (e) {
      console.error(e);
    }

    machinelogState.reload();
  }

  if (isParseObject(origin, Schedule_Execution.className)) {
    const object = origin as Schedule_Execution;

    return (
      <div>
        <Flex gap="small">
          <Button
            shape="circle"
            icon={<Icon icon="fa:eye" />}
            onClick={() => setOpenViewModal(true)}
          />
          <Button
            shape="circle"
            onClick={() => {
              setOpenEditModal(true);
            }}
          >
            <Icon icon="fa:pen" />
          </Button>
          <Popconfirm
            title={t("maintenance:machinelog.entry.button.delete.title")}
            description={t(
              "maintenance:machinelog.entry.button.delete.description"
            )}
            onConfirm={() => removeMessage()}
            onCancel={() => {}}
            okText={t("maintenance:global.button.ok_label")}
            cancelText={t("maintenance:global.button.close_label")}
          >
            <Button shape="circle" icon={<Icon icon="fa:trash" />} />
          </Popconfirm>
        </Flex>

        <Drawer
          open={openViewModal}
          onClose={() => setOpenViewModal(false)}
          closable
        >
          <ScheduleExecutionDetails execution={object} />
        </Drawer>
        <SpontaneousScheduleModal
          sources={[object.get("source") as Source]}
          editExecution={object}
          onClose={() => {
            setOpenEditModal(false);
          }}
          onSaveSuccess={() => {
            setOpenEditModal(false);
            message.success(
              object
                ? t("maintenance:schedules.update-success")
                : t("maintenance:schedules.save-success")
            );
          }}
          open={openEditModal}
        />
      </div>
    );
  }

  if (isParseObject(origin, Message.className)) {
    const object = origin as Message;
    return (
      <div>
        <Flex gap="small">
          <Button
            shape="circle"
            onClick={() => setOpenViewModal(true)}
            icon={<Icon icon="fa:eye" />}
          />
          <Button
            shape="circle"
            onClick={() => setOpenEditModal(true)}
            icon={<Icon icon="fa:pen" />}
          />
          <Popconfirm
            title={t("maintenance:machinelog.entry.button.delete.title")}
            description={t(
              "maintenance:machinelog.entry.button.delete.description"
            )}
            onConfirm={() => removeMessage()}
            onCancel={() => {}}
            okText={t("maintenance:global.button.ok_label")}
            cancelText={t("maintenance:global.button.close_label")}
          >
            <Button shape="circle" icon={<Icon icon="fa:trash" />} />
          </Popconfirm>
        </Flex>
        <Drawer
          open={openViewModal}
          onClose={() => setOpenViewModal(false)}
          closable
          width="40%"
        >
          {object && <MessageDynamicDescription message={object} />}
        </Drawer>
        <Modal
          open={openEditModal}
          onCancel={() => setOpenEditModal(false)}
          footer={null}
        >
          <MessageForm
            message={object}
            onSave={() => setOpenEditModal(false)}
            onError={() => setOpenEditModal(false)}
          />
        </Modal>
      </div>
    );
  }

  const object = origin as TicketData;
  return (
    <div>
      <Flex gap="small">
        {object?.ticket && (
          <>
            <Button
              shape="circle"
              onClick={() => setOpenViewModal(true)}
              icon={<Icon icon="fa:eye" />}
            />
            <Button
              shape="circle"
              onClick={() => nav("/openservice/ticket/" + object?.ticket?.id)}
            >
              <Icon icon="fa:pen" />
            </Button>
          </>
        )}
        <Popconfirm
          title={t("maintenance:machinelog.entry.button.delete.title")}
          description={t(
            "maintenance:machinelog.entry.button.delete.description"
          )}
          onConfirm={() => removeMessage()}
          onCancel={() => {}}
          okText={t("maintenance:global.button.ok_label")}
          cancelText={t("maintenance:global.button.close_label")}
        >
          <Button shape="circle" icon={<Icon icon="fa:trash" />} />
        </Popconfirm>
      </Flex>

      {object?.ticket && (
        <Drawer
          open={openViewModal}
          onClose={() => setOpenViewModal(false)}
          closable
          width="40%"
        >
          <TicketContextProvider value={object}>
            <TicketDynamicDescription bordered column={1} layout="vertical" />
          </TicketContextProvider>
        </Drawer>
      )}
    </div>
  );
});
